import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import {
  Button,
  Flex,
  Typography,
  Upload,
  Image,
  Card,
  Collapse,
  Dropdown,
  Input,
  Form,
  Select,
  message,
  GetProp,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CloseCircleTwoTone,
  CreditCardOutlined,
  FileTextOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { Document, Page, pdfjs } from "react-pdf";
import { PresupuestoIcon } from "../icon";
import { axiosInstance, billingDisplayName, formatPrice, nombreDeEntrega } from "@/utilities";
import {
  API_URL_GET_INVOICE,
  API_URL_GET_RECEIPT,
  API_URL_PAYMENTS,
  API_URL_SHIPMENT,
} from "@/services/XCONS_ENV";
import { HandCash } from "../icon/HandCash";
import { MercadoPago } from "../icon/MercadoPago";
import { ECheck } from "../icon/ECheck";
import { OrderSteps } from "./invoice-steps";
import CreditCardForm from "./form-fiserv";
import { CreditMemo, CreditMemoPanel } from "./credit-memo";
import { ManualExtraCharges } from "./manual-extra-charges";

// Configurar el worker de pdf.js usando CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const { Text } = Typography;

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

type Props = {
  item: any;
  invoice?: any;
  delivery?: any;
  balance?: number;
  billingAddress?: any;
  statusOrder: string[];
  updateOrderData: any;
  creditMemos?: any;
  shipments?: any;
  showManualExtraCharges?: boolean;
  orderId?: number;
  orderStatus?: string | null;
  additionalPayment?: number | null;
};

export const OrderPaymentItem: React.FC<Props> = ({
  item,
  invoice,
  delivery,
  balance,
  billingAddress,
  statusOrder,
  updateOrderData,
  creditMemos,
  shipments,
  showManualExtraCharges,
  orderId,
  orderStatus,
  additionalPayment,
}) => {
  // Estados exclusivos para cada pago
  const [fileList, setFileList] = useState<any[]>([]);
  const [invoiceFileList, setInvoiceFileList] = useState<any[]>([]);
  const [loadingStep, setLoadingStep] = useState(false);
  const [uploadReceipt, setUploadReceipt] = useState(false);
  const [uploadInvoice, setUploadInvoice] = useState(false);
  const [canSendReview, setCanSendReview] = useState(false);
  const [canSendInvoice, setCanSendInvoice] = useState(false);
  const [openCancelPaymentModal, setOpenCancelPaymentModal] = useState(false);
  const [collapseActiveKey, setCollapseActiveKey] = useState<string[]>([item.paymentId.toString()]);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail?.paymentId === item.paymentId) {
        setCollapseActiveKey([item.paymentId.toString()]);
      }
    };
    window.addEventListener("openPaymentPanel", handler as EventListener);
    return () => window.removeEventListener("openPaymentPanel", handler as EventListener);
  }, [item.paymentId]);

  const [currentStep, setCurrentStep] = useState(0);

  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState("");
  const [previewPdfOpen, setPreviewPdfOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [previewFilename, setPreviewFilename] = useState("");

  const [selectedPayment, setSelectedPayment] = useState(
    item?.paymentMethod || "",
  );
  const [form] = Form.useForm();

  // Helpers
  const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  // Detecta el tipo MIME real leyendo los primeros bytes del blob (ignora la extensión del nombre)
  const detectMimeType = (blob: Blob): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = (e) => {
        const arr = new Uint8Array(e.target?.result as ArrayBuffer);
        const hex = Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
        if (hex.startsWith("25504446")) resolve("application/pdf");
        else if (hex.startsWith("ffd8ff")) resolve("image/jpeg");
        else if (hex.startsWith("89504e47")) resolve("image/png");
        else if (hex.startsWith("47494638")) resolve("image/gif");
        else resolve(blob.type || "application/octet-stream");
      };
      reader.readAsArrayBuffer(blob.slice(0, 4));
    });

  const MIME_EXT: Record<string, string> = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
  };

  // Corrige la extensión del nombre de archivo si no coincide con el MIME real
  const correctFilename = (name: string, mimeType: string): string => {
    const correctExt = MIME_EXT[mimeType];
    if (!correctExt) return name;
    const currentExt = name.split(".").pop()?.toLowerCase();
    const validExts: Record<string, string[]> = {
      pdf: ["pdf"], jpg: ["jpg", "jpeg"], png: ["png"], gif: ["gif"],
    };
    if (validExts[correctExt]?.includes(currentExt ?? "")) return name;
    return name.replace(/\.[^.]+$/, "") + "." + correctExt;
  };

  const handlePreview = async (file: UploadFile) => {
    // Detectar si es PDF usando tipo MIME o extensión
    const isPdf = file.type === "application/pdf";

    let fileUrl = file.url;

    if (!fileUrl && file.originFileObj) {
      if (isPdf) {
        // Para PDFs, crear un blob URL
        fileUrl = URL.createObjectURL(file.originFileObj);
      } else {
        // Para imágenes, usar base64
        fileUrl = await getBase64(file.originFileObj as FileType);
      }
    }

    // Agregar #toolbar=0 para PDFs para evitar descarga automática
    if (isPdf && fileUrl && !fileUrl.includes("#")) {
      fileUrl = `${fileUrl}#toolbar=0`;
    }

    if (isPdf) {
      setPreviewPdf(fileUrl || "");
      setPreviewFilename(file.name || "documento.pdf");
      setPreviewPdfOpen(true);
    } else {
      setPreviewImage(fileUrl || "");
      setPreviewOpen(true);
    }
  };

  const handleBeforeUpload = (file: FileType) => {
    // Puedes cambiar el '15' por la cantidad de Megabytes máximos que desees permitir
    const limitMB = 15;
    const isLessThanLimit = file.size / 1024 / 1024 < limitMB;

    if (!isLessThanLimit) {
      message.error(`El archivo "${file.name}" excede el tamaño máximo permitido de ${limitMB}MB`);
    }

    // Retornar Upload.LIST_IGNORE evita que el archivo se agregue al componente
    return isLessThanLimit ? true : Upload.LIST_IGNORE;
  };

  const handleRemoveFile = (file: any) => {
    setFileList((prev: any[]) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleRemoveReceipt = (file: any): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("orders.receipt.delete.confirm.title"),
        content: t("orders.receipt.delete.confirm.content"),
        okText: t("buttons.confirm"),
        cancelText: t("buttons.back"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(`${API_URL_GET_RECEIPT}/${file.uid}`);
            setFileList((prev: any[]) => prev.filter((f) => f.uid !== file.uid));
            message.success(t("orders.receipt.delete.success"));
            updateOrderData?.();
            resolve(true);
          } catch (error: any) {
            if (error.message === "Insufficient permissions") {
              message.error(t("insufficient.permissions"));
            } else {
              message.error(t("orders.receipt.delete.failed"));
            }
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  const handleRemoveInvoice = (file: any): Promise<boolean> => {
    // Archivo staged (sin factura persistida en backend): quitarlo localmente,
    // sin DELETE y sin tocar los inputs de número/punto de venta.
    if ((item?.invoices?.length ?? 0) === 0) {
      setInvoiceFileList((prev: any[]) =>
        prev.filter((f) => f.uid !== file.uid),
      );
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("orders.invoice.delete.confirm.title"),
        content: t("orders.invoice.delete.confirm.content"),
        okText: t("buttons.confirm"),
        cancelText: t("buttons.back"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(`${API_URL_GET_INVOICE}/${file.uid}`);
            setInvoiceFileList((prev: any[]) => prev.filter((f) => f.uid !== file.uid));
            message.success(t("orders.invoice.delete.success"));
            updateOrderData?.();
            resolve(true);
          } catch (error: any) {
            if (error.message === "Insufficient permissions") {
              message.error(t("insufficient.permissions"));
            } else {
              message.error(t("orders.invoice.delete.failed"));
            }
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  const paymentOptions = [
    {
      label: (
        <Flex gap={8} align="center">
          <BankOutlined />
          <Text strong>{t("paymentmethod.dropdown.banktransfer")}</Text>
        </Flex>
      ),
      value: "banktransfer",
    },
    {
      label: (
        <Flex gap={8} align="center">
          <ECheck />
          <Text strong>E-check</Text>
        </Flex>
      ),
      value: "checkmo",
    },
    {
      label: (
        <Flex gap={8} align="center">
          <CreditCardOutlined />
          <Text strong>FISERV Payment</Text>
        </Flex>
      ),
      value: "firstdata",
    },
    {
      label: (
        <Flex gap={8} align="center">
          <MercadoPago />
          <Text strong>Mercado Pago</Text>
        </Flex>
      ),
      value: "mercadopago_basic",
    },
    {
      label: (
        <Flex gap={8} align="center">
          <HandCash />
          <Text strong>{t("paymentmethod.dropdown.incash")}</Text>
        </Flex>
      ),
      value: "purchaseorder",
    },
    {
      label: (
        <Flex gap={8} align="center">
          <HandCash />
          <Text strong>GetNet</Text>
        </Flex>
      ),
      value: "argenmagento",
    },
  ];

  // --- Loaders específicos de cada pago
  const getAllReceipts = async (
    receipts: { receiptId: number; originalFilename: string }[] = [],
  ) => {
    try {
      const files = await Promise.all(
        receipts.map(async (receipt) => {
          const res = await axiosInstance.get(
            `${API_URL_GET_RECEIPT}/${receipt.receiptId}`,
            { responseType: "blob" },
          );
          const mimeType = await detectMimeType(res.data);
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);
          return {
            uid: receipt.receiptId.toString(),
            name: correctFilename(receipt.originalFilename || "Receipt", mimeType),
            status: "done",
            url,
            type: mimeType,
          };
        }),
      );
      setFileList(files);
    } catch (error) {
      console.error(error);
    }
  };

  const getAllInvoices = async (
    invoices: { invoiceId: number; originalFilename: string }[] = [],
  ) => {
    try {
      const files = await Promise.all(
        invoices.map(async (invoice) => {
          const res = await axiosInstance.get(
            `${API_URL_GET_INVOICE}/${invoice.invoiceId}`,
            { responseType: "blob" },
          );
          const mimeType = await detectMimeType(res.data);
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);
          return {
            uid: invoice.invoiceId.toString(),
            name: correctFilename(invoice.originalFilename || "Invoice", mimeType),
            status: "done",
            url,
            type: mimeType,
          };
        }),
      );
      setInvoiceFileList(files);
    } catch (error) {
      console.error(error);
    }
  };

  // --- Uploads específicos
  const handleReceiptUpload = async (options: any) => {
    const { file } = options;
    try {
      const values = form.getFieldsValue(["receipt", "number"]);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("pointOfSale", values.receipt);
      formData.append("number", item?.amount);
      await axiosInstance.post(
        `${API_URL_PAYMENTS}/${item?.paymentId}/receipts`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      message.success(t("common.messages.receiptUploaded"));
      setCanSendReview(true);
      updateOrderData?.();
    } catch (error: any) {
      if (error.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
      } else {
        message.error(t("common.messages.receiptUploadFailed"));
      }
    }
  };

  // POST real de la factura (archivo + número/punto de venta si es la primera).
  const doInvoicePost = async (file: any) => {
    const hasPrimaryInvoice = (item?.invoices?.length ?? 0) > 0;
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (!hasPrimaryInvoice) {
        const values = form.getFieldsValue(["invoice_number", "number_code"]);
        const pointOfSale = parseInt(values.invoice_number, 10);
        const number = parseInt(values.number_code, 10);
        formData.append("pointOfSale", pointOfSale.toString());
        formData.append("number", number.toString());
      }
      await axiosInstance.post(
        `${API_URL_PAYMENTS}/${item?.paymentId}/invoices`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      message.success(t("common.messages.invoiceUploaded"));
      setCanSendInvoice(true);
      updateOrderData?.();
    } catch (error) {
      message.error(t("common.messages.invoiceUploadFailed"));
    }
  };

  // Al subir el comprobante: SOLO se guarda (persiste en back + S3). No cambia
  // el estado del pago. Para la primera factura se requieren los números.
  const handleInvoiceUpload = async (options: any) => {
    const { file } = options;
    const hasPrimaryInvoice = (item?.invoices?.length ?? 0) > 0;
    const values = form.getFieldsValue(["invoice_number", "number_code"]);
    if (!hasPrimaryInvoice && (!values.invoice_number || !values.number_code)) {
      message.warning(t("orders.payment.completeInvoiceNumbers"));
      return;
    }
    setInvoiceFileList([
      {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(file),
        originFileObj: file,
        type:
          file.type ||
          (file.name?.toLowerCase().endsWith(".pdf")
            ? "application/pdf"
            : "image/jpeg"),
      },
    ]);
    await doInvoicePost(file);
  };

  // (Re)envía la factura ya cargada a Magento, SIN cambiar el estado del pago.
  const handleResendInvoice = async () => {
    if (!item?.paymentId) return;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_PAYMENTS}/${item.paymentId}/invoices/resend`,
      );
      message.success(t("orders.payment.invoiceSent"));
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || t("orders.payment.invoiceSendError"),
      );
    } finally {
      setLoadingStep(false);
    }
  };

  const handleChangeStep = async (
    status: string,
    extraPayload: Record<string, any> = {},
  ) => {
    setLoadingStep(true);
    try {
      const payload = {
        status,
        ...extraPayload,
      };

      await axiosInstance.patch(
        `${API_URL_PAYMENTS}/${item?.paymentId}`,
        payload,
      );

      // if (
      //   status === "canceled" &&
      //   Array.isArray(shipments) &&
      //   shipments.length
      // ) {
      //   const toCancel = shipments.filter(
      //     (s) => String(s?.status) !== "canceled",
      //   );

      //   if (toCancel.length) {
      //     await Promise.all(
      //       toCancel.map((s) =>
      //         axiosInstance.patch(`${API_URL_SHIPMENT}/${s.shipmentId}`, {
      //           status: "canceled",
      //         }),
      //       ),
      //     );
      //   }
      // }
      updateOrderData?.();
      message.success(t("common.messages.paymentStatusUpdated"));
    } catch (error: any) {
      if (error.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
      } else {
        message.error(t("common.messages.paymentStatusUpdateFailed"));
      }
    } finally {
      setLoadingStep(false);
    }
  };

  // Acepta la solicitud de cancelación del customer → ejecuta la cancelación
  // real (Magento canceled + cancela shipments) del lado del backend.
  const handleConfirmCancelRequest = async () => {
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_PAYMENTS}/${item?.paymentId}/cancel-confirm`,
      );
      updateOrderData?.();
      message.success(t("orders.payment.cancelRequest.confirmedMsg"));
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          t("orders.payment.cancelRequest.errorMsg"),
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Rechaza la solicitud → el pago vuelve a su estado original (sin tocar Magento).
  const handleRejectCancelRequest = async () => {
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_PAYMENTS}/${item?.paymentId}/cancel-reject`,
      );
      updateOrderData?.();
      message.success(t("orders.payment.cancelRequest.rejectedMsg"));
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          t("orders.payment.cancelRequest.errorMsg"),
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // --- Actualiza todos los estados necesarios solo para este pago
  useEffect(() => {
    setCanSendReview(selectedPayment === "mercadopago_basic" || uploadReceipt);
    setSelectedPayment(item?.paymentMethod);
    // En pending_cancel (solicitud del customer) el stepper queda congelado en
    // el estado original guardado en metadata, no en un step inválido (-1).
    setCurrentStep(
      statusOrder.indexOf(
        item?.status === "pending_cancel"
          ? item?.metadata?.cancelRequest?.originalStatus ?? item?.status
          : item?.status,
      ),
    );
    if (item?.invoices?.length) {
      getAllInvoices(item.invoices);
      setCanSendInvoice(true);
    } else {
      setCanSendInvoice(false);
    }
    if (item?.receipts) {
      getAllReceipts(item.receipts);
      setCanSendReview(true);
    }
  }, [item]);

  useEffect(() => {
    const primaryInvoice = item?.invoices?.[0];
    const meta = item?.metadata ?? {};
    form.setFieldsValue({
      receipt: meta.receipt || "",
      number: meta.receiptAmount || "",
      // Con factura cargada manda el documento: es lo que se emitió y lo que
      // viaja a Magento. `metadata` es el borrador previo a la primera carga
      // —lo que el operador tipea y el POST se lleva— y sólo gobierna mientras
      // no haya factura. Al revés, la pantalla mostraba un número y el
      // comprobante tenía otro (xcons-docs#346).
      invoice_number: primaryInvoice?.pointOfSale ?? meta.invoicePointOfSale ?? "",
      number_code: primaryInvoice?.number ?? meta.invoiceNumber ?? "",
    });
  }, [item, form]);

  // Persiste número + punto de venta de la factura. Se llama on blur de cada input.
  //
  // Dos destinos, y hay que escribir en los dos (xcons-docs#346):
  //   - `payment.metadata`: el borrador. Es lo que el POST de la PRIMERA factura
  //     se lleva, y sobrevive al borrado del comprobante y al reload.
  //   - el documento, vía `PATCH /payments/invoices/:invoiceId`: recién esto
  //     cambia la factura emitida. Sin esta mitad el campo se dejaba editar,
  //     mostraba el valor nuevo y el comprobante seguía con el viejo.
  //
  // La última palabra la tiene el backend: si la factura ya está publicada en
  // Magento contesta 409 —el número viaja dentro del `custom_id`— y ahí se
  // muestra su mensaje y el campo vuelve al valor del documento.
  const saveInvoiceNumbers = async () => {
    if (!item?.paymentId) return;
    const v = form.getFieldsValue(["invoice_number", "number_code"]);
    const pointOfSale = (v.invoice_number ?? "").toString().trim();
    const number = (v.number_code ?? "").toString().trim();

    try {
      await axiosInstance.patch(`${API_URL_PAYMENTS}/${item.paymentId}`, {
        metadata: {
          invoicePointOfSale: pointOfSale,
          invoiceNumber: number,
        },
      });
    } catch {
      // silencioso: es autosave; no interrumpe la carga
    }

    const invoice = item?.invoices?.[0];
    if (!invoice?.invoiceId) return;

    const sinCambios =
      pointOfSale === ((invoice.pointOfSale ?? "") as string) &&
      number === ((invoice.number ?? "") as string);
    if (sinCambios) return;

    if (!/^\d+$/.test(pointOfSale) || !/^\d+$/.test(number)) {
      message.error(t("orders.documents.numbersOnlyDigits"));
      return;
    }

    try {
      await axiosInstance.patch(`${API_URL_GET_INVOICE}/${invoice.invoiceId}`, {
        pointOfSale,
        number,
      });
      message.success(t("orders.documents.numbersSaved"));
      updateOrderData?.();
    } catch (error: any) {
      // El 409 manda `message` como string; el 400 de validación, como array.
      const detalle = error?.response?.data?.message;
      message.error(
        (Array.isArray(detalle) ? detalle.join(" ") : detalle) ||
          t("orders.documents.numbersFailed"),
      );
      form.setFieldsValue({
        invoice_number: invoice.pointOfSale ?? "",
        number_code: invoice.number ?? "",
      });
    }
  };

  // Estos form fields son individuales por pago
  const receiptValue = Form.useWatch("receipt", form);
  const invoiceNumberValue = Form.useWatch("invoice_number", form);
  const invoiceNumberCodeValue = Form.useWatch("number_code", form);

  const cantInitPayment = true;

  const BACK_TO_STATE =
    item?.status === "review"
      ? "pending"
      : item?.status === "verified"
        ? "review"
        : "";

  const hideBackToState =
    BACK_TO_STATE === "" ||
    (item?.paymentMethod === "firstdata" && currentStep >= 3) ||
    (item?.paymentMethod === "purchaseorder" && currentStep >= 2);

  const shipmentsById = useMemo(() => {
    const map: Record<number, any> = {};
    for (const s of shipments ?? []) {
      map[Number(s?.shipmentId)] = s;
    }
    return map;
  }, [shipments]);

  const enrichedCreditMemos = useMemo(() => {
    return (creditMemos ?? []).map((m: CreditMemo & any) => {
      const canceledId = Number(
        m?.canceledShipmentId ?? m?.metadata?.sourceShipmentId,
      );
      const ship = shipmentsById[canceledId];
      // Para NCs nuevas (bulk, shippingOnly) el grandTotal vive en
      // memo.metadata. Para NCs individuales viejas vive en shipment.metadata.
      // memo.metadata puede llegar como string si no pasó por el transformer.
      const parsedMemoMeta = (() => {
        const raw = m?.metadata;
        if (!raw) return null;
        if (typeof raw === "string") {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        }
        return raw;
      })();
      const memoGrandTotal =
        parsedMemoMeta?.grandTotal != null
          ? Number(parsedMemoMeta.grandTotal)
          : null;
      const shipGrandTotal =
        ship?.metadata?.grandTotal != null
          ? Number(ship.metadata.grandTotal)
          : null;
      const amount =
        memoGrandTotal != null && memoGrandTotal > 0
          ? memoGrandTotal
          : shipGrandTotal != null
            ? shipGrandTotal
            : 0;
      const shipmentName =
        nombreDeEntrega(ship?.metadata?.name) || `Shipment #${canceledId || "-"}`;

      // `deliveryGroup` puede no estar en el metadata del canceled (caso
      // partial creado antes de copiarlo del source, o creditmemos viejos).
      // Lo resolvemos cruzando con el source shipment via sourceShipmentId.
      const directDg = ship?.metadata?.deliveryGroup;
      let resolvedDeliveryGroup: number | null =
        directDg != null ? Number(directDg) : null;
      if (resolvedDeliveryGroup == null) {
        const sourceId = Number(ship?.metadata?.sourceShipmentId);
        const sourceShip = sourceId ? shipmentsById[sourceId] : undefined;
        const sourceDg = sourceShip?.metadata?.deliveryGroup;
        if (sourceDg != null) resolvedDeliveryGroup = Number(sourceDg);
      }

      // Las entregas que canceló la nota, para el principio de su flujo. Una cancelación de
      // varias entregas a la vez —la total del pedido— marca todas con el mismo
      // `bulkCancelRequest.groupId`, pero la nota apunta a una sola: se juntan por el grupo
      // o, si no está, por los números de entrega de la nota.
      const grupo = ship?.metadata?.bulkCancelRequest?.groupId;
      const numerosDeEntrega: number[] = (
        parsedMemoMeta?.bulkDeliveryGroups ??
        ship?.metadata?.bulkDeliveryGroups ??
        []
      ).map(Number);
      const canceladas = (shipments ?? []).filter(
        (s: any) => s?.status === "canceled",
      );
      const delGrupo = grupo
        ? canceladas.filter(
            (s: any) => s?.metadata?.bulkCancelRequest?.groupId === grupo,
          )
        : numerosDeEntrega.length
          ? canceladas.filter((s: any) =>
              numerosDeEntrega.includes(Number(s?.metadata?.deliveryGroup)),
            )
          : [];
      const entregasCanceladas = (delGrupo.length ? delGrupo : ship ? [ship] : [])
        .map((s: any) => ({
          id: s?.shipmentId,
          name:
            nombreDeEntrega(s?.metadata?.name) ||
            `Shipment #${s?.shipmentId ?? "-"}`,
        }));

      return {
        ...m,
        canceledShipmentId: canceledId,
        entregasCanceladas,
        shipment: ship,
        amount,
        shipmentName,
        resolvedDeliveryGroup,
      };
    });
  }, [creditMemos, shipmentsById]);

  // Cantidad de entregas distintas del pedido (basado en deliveryGroup del
  // metadata). Lo usa el label del CreditMemoPanel para decidir si una
  // cancelación total corresponde a "la factura del pedido" (una sola
  // entrega) o "la entrega N".
  const totalDeliveryGroups = useMemo(() => {
    const groups = new Set<number>();
    (shipments ?? []).forEach((s: any) => {
      const dg = s?.metadata?.deliveryGroup;
      if (dg != null) groups.add(Number(dg));
    });
    return groups.size;
  }, [shipments]);

  // Memoizar las opciones del PDF para evitar recargas innecesarias
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  return (
    <>
      <Collapse
        activeKey={collapseActiveKey}
        onChange={(keys) => setCollapseActiveKey(Array.isArray(keys) ? keys : [keys])}
        className={`${item?.status === "canceled"
          ? "custom-header-collapse-white"
          : "custom-header-collapse"
          }`}
        items={[
          {
            key: item.paymentId.toString(),
            label: (
              <Flex justify="space-between" align="center">
                <Flex gap={6}>
                  <FileTextOutlined style={{ color: "white" }} />
                  <Text strong style={{ color: "white" }}>
                    {invoice?.customId
                      ? invoice?.customId
                      : t("orders.label.orderinvoice")}
                  </Text>
                </Flex>
                <Flex align="center" gap={16}>
                  <Text strong style={{ color: "white" }}>
                    Balance: {formatPrice(Number(balance))}
                  </Text>
                  {item?.status !== "canceled" &&
                    item?.status !== "invoiced" &&
                    // En pending_cancel la acción vive en el banner de la
                    // solicitud (Confirmar / Rechazar), no en el dropdown.
                    item?.status !== "pending_cancel" && (
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "1",
                              style: {
                                // Show only when there IS a valid back-to-state
                                // for the current payment status (review → pending,
                                // verified → review). `hideBackToState` is true
                                // when BACK_TO_STATE === "" (no valid previous
                                // state), so the previous block/none was inverted.
                                display: hideBackToState ? "none" : "block",
                              },
                              label: (
                                <Text>
                                  {item?.status === "review"
                                    ? t("orders.Return oPendingPayment")
                                    : t("orders.label.ReturnToPaymentReview")}
                                </Text>
                              ),
                              onClick: (e) => {
                                e.domEvent.stopPropagation();
                                handleChangeStep(BACK_TO_STATE);
                              },
                            },
                            {
                              key: "2",
                              disabled: item.status === "invoiced",
                              label: (
                                <Text type="danger">
                                  {t("orders.label.cancelTransaction")}
                                </Text>
                              ),
                              style: {
                                display:
                                  item?.status === "canceled" ||
                                    item?.status === "verified" ||
                                    (item?.paymentMethod === "firstdata" &&
                                      currentStep === 2)
                                    ? "none"
                                    : "block",
                              },
                              onClick: (e) => {
                                e.domEvent.stopPropagation();
                                setOpenCancelPaymentModal(true);
                              },
                            },
                          ],
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                          },
                        }}
                      >
                        <Button
                          type="link"
                          icon={<MoreOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          style={{ color: "white" }}
                        />
                      </Dropdown>
                    )}

                  {item?.status === "canceled" && (
                    <CloseCircleTwoTone
                      twoToneColor={"red"}
                      style={{ transform: "scale(1.4)" }}
                    />
                  )}
                </Flex>
              </Flex>
            ),
            children: (
              <>
                {item?.status === "pending_cancel" && (
                  <Card
                    style={{ background: "#fff2f0", marginBottom: 16 }}
                    styles={{ body: { padding: 16 } }}
                  >
                    <Flex vertical gap={12}>
                      <Flex vertical gap={4}>
                        <Text strong>
                          {t("orders.payment.cancelRequest.pendingTitle")}
                        </Text>
                        {item?.metadata?.cancelRequest?.reason && (
                          <Text type="secondary">
                            {item.metadata.cancelRequest.reason}
                          </Text>
                        )}
                      </Flex>
                      <Flex align="center" justify="space-between" gap={8}>
                        <Button
                          icon={<ArrowLeftOutlined />}
                          loading={loadingStep}
                          onClick={handleRejectCancelRequest}
                        >
                          {t("orders.payment.cancelRequest.reject")}
                        </Button>
                        <Button
                          type="primary"
                          danger
                          loading={loadingStep}
                          onClick={() => {
                            Modal.confirm({
                              title: t(
                                "orders.payment.cancelRequest.confirm.modal.title",
                              ),
                              content: t(
                                "orders.payment.cancelRequest.confirm.modal.content",
                              ),
                              okText: t("orders.payment.cancelRequest.confirm"),
                              cancelText: t("buttons.back"),
                              okButtonProps: { danger: true },
                              cancelButtonProps: {
                                icon: <ArrowLeftOutlined />,
                              },
                              onOk: handleConfirmCancelRequest,
                            });
                          }}
                        >
                          {t("orders.payment.cancelRequest.confirm")}
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                )}
                <OrderSteps
                  paymentId={item?.paymentId}
                  loadingStep={loadingStep}
                  currentStep={currentStep}
                  stepStatus={statusOrder}
                  paymentStatus={item?.status}
                  cantInitPayment={cantInitPayment}
                  canSendReview={canSendReview}
                  uploadInvoice={uploadInvoice}
                  canSendInvoice={canSendInvoice}
                  handleChangeStep={(status, extraPayload) =>
                    handleChangeStep(status, extraPayload)
                  }
                  delivery={delivery}
                  selectedPayment={selectedPayment}
                />

                <Card className="tarjeta-columnas">
                  <Flex justify="space-between" style={{ width: "100%" }}>
                    <Flex
                      vertical
                      className="tarjeta-columnas__columna"
                      style={{ flexGrow: 2 }}
                    >
                      <Flex justify="space-between">
                        <Flex align="center" gap={8}>
                          <PresupuestoIcon />
                          <Text strong>
                            {t("orders.tag.invoice")} "{`${billingAddress?.billingType?.slice(0, 1).toUpperCase()}`}" {" "}
                            {item?.status !== "invoiced" && (
                              <Text italic style={{ color: "#FF8F1F" }}>
                                {t("orders-label.pending")}
                              </Text>
                            )}
                          </Text>
                        </Flex>
                        <Text strong>{formatPrice(item?.amount)}</Text>
                      </Flex>
                      <Flex vertical>
                        <Flex style={{ margin: "16px 0 8px" }}>
                          <span>{`${billingDisplayName(billingAddress)} ${billingAddress?.vatId} | ${billingAddress?.street}`}</span>
                        </Flex>
                        {showManualExtraCharges && orderId != null && (
                          <ManualExtraCharges
                            orderId={orderId}
                            orderStatus={orderStatus}
                            currentValue={additionalPayment}
                            onSaved={updateOrderData}
                          />
                        )}
                        {currentStep >= 2 && (
                          <Flex>
                            <Form
                              form={form}
                              layout="vertical"
                              initialValues={{
                                invoice_number:
                                  item?.invoices?.[0]?.pointOfSale ??
                                  item?.metadata?.invoicePointOfSale ??
                                  "",
                                number_code:
                                  item?.invoices?.[0]?.number ??
                                  item?.metadata?.invoiceNumber ??
                                  "",
                              }}
                            >
                              <Flex gap={8} align="end">
                                <Form.Item
                                  label={
                                    <Text type="secondary">
                                      {t("orders.label.invoicenumber")}
                                    </Text>
                                  }
                                  name="invoice_number"
                                  rules={[{ required: true }]}
                                >
                                  <Input
                                    placeholder={
                                      item?.status === "canceled"
                                        ? "-"
                                        : "Ex: 00001"
                                    }
                                    type="text"
                                    onBlur={saveInvoiceNumbers}
                                  />
                                </Form.Item>
                                <Form.Item
                                  name="number_code"
                                  rules={[{ required: true }]}
                                >
                                  <Input
                                    placeholder={
                                      item?.status === "canceled"
                                        ? "-"
                                        : "Ex: 00036548"
                                    }
                                    type="text"
                                    onBlur={saveInvoiceNumbers}
                                  />
                                </Form.Item>
                              </Flex>

                              <Form.Item
                                label={
                                  <Text type="secondary">
                                    {t("orders.tag.invoice")}
                                  </Text>
                                }
                                name="contacts"
                                rules={[{ required: true }]}
                              >
                                <Upload
                                  id="invoice-upload"
                                  listType="picture-card"
                                  accept="image/*,.pdf"
                                  customRequest={handleInvoiceUpload}
                                  beforeUpload={handleBeforeUpload}
                                  onPreview={handlePreview}
                                  fileList={invoiceFileList}
                                  onRemove={handleRemoveInvoice}
                                  showUploadList={{ showRemoveIcon: true }}
                                  // Sin gatear por los números: el archivo se
                                  // puede cargar en cualquier orden. Si faltan
                                  // los números, el upload queda diferido.
                                  disabled={currentStep !== 2}
                                >
                                  + {t("buttons.upload")}
                                </Upload>
                              </Form.Item>
                            </Form>
                          </Flex>
                        )}
                      </Flex>

                      {/* Reenviar factura a Magento (no cambia el estado del
                          pago). Solo en "Facturado - Aprobado". Secundario,
                          alineado a la derecha de la sección de factura. */}
                      {item?.status === "invoiced" && (
                        <Flex justify="end" style={{ marginTop: 16 }}>
                          <Button
                            size="small"
                            loading={loadingStep}
                            onClick={handleResendInvoice}
                          >
                            {t("orders.payment.resendInvoice")}
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    <Flex
                      vertical
                      className="tarjeta-columnas__columna"
                      style={{ flexGrow: 2 }}
                      gap={8}
                    >
                      <Flex
                        align="baseline"
                        justify="space-between"
                        gap={4}
                        style={{ marginBottom: 12, width: "100%" }}
                      >
                        <Flex gap={8}>
                          <Select
                            value={selectedPayment}
                            onChange={setSelectedPayment}
                            style={{ width: 250 }}
                            options={paymentOptions}
                            disabled
                          />
                        </Flex>
                        <Flex>
                          <Text
                            strong
                            style={{
                              color: currentStep === 1 ? "#FF8F1F" : "",
                            }}
                          >
                            {Number(balance) > 0
                              ? `-${formatPrice(Number(balance))}`
                              : `- ${formatPrice(item?.amount)}`}
                          </Text>
                        </Flex>
                      </Flex>
                      <Flex vertical>
                        {selectedPayment !== "firstdata" &&
                          selectedPayment !== "mercadopago_basic" && (
                            <Form
                              form={form}
                              layout="vertical"
                              initialValues={{
                                receipt: item?.metadata?.receipt || "",
                                number: item?.amount || "",
                              }}
                            >
                              <Flex gap={24}>
                                <Form.Item
                                  label={
                                    <Text type="secondary">
                                      {t("orders.label.receipt")}
                                    </Text>
                                  }
                                  name="receipt"
                                >
                                  <Flex vertical>
                                    <Input
                                      placeholder="-"
                                      type="text"
                                      disabled={currentStep !== 0}
                                    />
                                  </Flex>
                                </Form.Item>
                                <Form.Item
                                  label={
                                    <Text type="secondary">
                                      {t("orders.label.amount")}
                                    </Text>
                                  }
                                  name="number"
                                >
                                  <Flex vertical>
                                    <Input
                                      placeholder="$"
                                      type="text"
                                      value={formatPrice(item?.amount) || "$"}
                                      disabled
                                    />
                                  </Flex>
                                </Form.Item>
                              </Flex>
                              <Flex vertical>
                                <Form.Item
                                  label={
                                    <Text type="secondary">
                                      {t("orders.label.paymentreceipt")}
                                    </Text>
                                  }
                                  name="receipt-upload"
                                >
                                  <Upload
                                    id="receipt-upload"
                                    listType="picture-card"
                                    accept="image/*,.pdf"
                                    customRequest={handleReceiptUpload}
                                    beforeUpload={handleBeforeUpload}
                                    onPreview={handlePreview}
                                    fileList={fileList}
                                    onRemove={handleRemoveReceipt}
                                    showUploadList={{ showRemoveIcon: true }}
                                    disabled={currentStep !== 0}
                                  >
                                    + {t("buttons.upload")}
                                  </Upload>
                                </Form.Item>
                              </Flex>
                            </Form>
                          )}
                        {(selectedPayment === "firstdata" ||
                          selectedPayment === "mercadopago_basic") && (
                            <CreditCardForm
                              dataPayment={item}
                              selectedPayment={selectedPayment}
                            />
                          )}
                      </Flex>
                      <Image
                        style={{ display: "none" }}
                        preview={{
                          visible: previewOpen,
                          onVisibleChange: (visible) => setPreviewOpen(visible),
                          afterOpenChange: (visible) =>
                            !visible && setPreviewImage(""),
                        }}
                        src={previewImage}
                      />

                      {/* Modal para previsualizar PDFs */}
                      <Modal
                        open={previewPdfOpen}
                        title="Vista previa del PDF"
                        footer={
                          <Flex justify="end" align="center">

                            <Button
                              type="primary"
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = previewPdf;
                                link.download = previewFilename;
                                link.click();
                              }}
                            >
                              Descargar PDF
                            </Button>
                          </Flex>
                        }
                        onCancel={() => {
                          setPreviewPdfOpen(false);
                          setPreviewPdf("");
                          setPreviewFilename("");
                          setNumPages(null);
                        }}
                        width={900}
                        centered
                        destroyOnClose
                        styles={{ body: { maxHeight: "75vh", overflowY: "auto", padding: "20px" } }}
                      >
                        <Document
                          key={previewPdf}
                          file={previewPdf}
                          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                          onLoadError={(error) => console.error("Error loading PDF:", error)}
                          options={pdfOptions}
                          loading={
                            <Flex justify="center" align="center" style={{ padding: "40px" }}>
                              <Text>Cargando PDF...</Text>
                            </Flex>
                          }
                          error={
                            <Flex
                              vertical
                              align="center"
                              justify="center"
                              style={{ padding: "40px" }}
                            >
                              <Text type="danger">
                                No se puede mostrar el PDF.
                              </Text>
                              <Button
                                type="primary"
                                style={{ marginTop: "16px" }}
                                onClick={() => {
                                  const link = document.createElement("a");
                                  link.href = previewPdf;
                                  link.download = "documento.pdf";
                                  link.click();
                                }}
                              >
                                Descargar PDF
                              </Button>
                            </Flex>
                          }
                        >
                          {numPages && Array.from(new Array(numPages), (el, index) => (
                            <Page
                              key={`page_${index + 1}`}
                              pageNumber={index + 1}
                              width={850}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          ))}
                        </Document>
                      </Modal>
                    </Flex>
                  </Flex>
                </Card>

                <Modal
                  open={openCancelPaymentModal}
                  title={t("orders.label.cancelTransaction")}
                  footer={null}
                  onCancel={() => setOpenCancelPaymentModal(false)}
                >
                  <Flex vertical>
                    <p>{t("notificacions.sureCancel")}</p>
                    <Flex justify="end" gap={16}>
                      <Button
                        onClick={() => setOpenCancelPaymentModal(false)}
                        icon={<ArrowLeftOutlined />}
                      >
                        {t("buttons.back")}
                      </Button>
                      <Button
                        loading={loadingStep}
                        type="primary"
                        danger
                        onClick={async () => {
                          await handleChangeStep("canceled");

                          setOpenCancelPaymentModal(false);
                        }}
                      >
                        {t("buttom.yesCancel")}
                      </Button>
                    </Flex>
                  </Flex>
                </Modal>
              </>
            ),
          },
        ]}
      />

      {/* nota de credito */}
      {enrichedCreditMemos.length > 0 &&
        enrichedCreditMemos.map((memo: any, index: number) => (
          <CreditMemoPanel
            key={`credit-${memo.creditMemoId}`}
            memo={memo}
            index={index}
            delivery={[memo.shipmentName]}
            shipment={memo.shipment}
            amount={memo.amount}
            totalDeliveryGroups={totalDeliveryGroups}
            handlePreview={handlePreview}
            handleRemoveFile={handleRemoveFile}
            paymentOptions={paymentOptions}
            updateOrderData={updateOrderData}
            billingAddress={billingAddress}
          />
        ))}
    </>
  );
};
